import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { PAGE_SIZE } from '../constants';
import { Button, Card, Empty, Input, Modal, Popconfirm, Popover, Segmented, Spin, Tag } from 'antd';
import { ReactSVG } from 'react-svg';
import queryString from 'query-string';
import { selectAllFleets } from '../modules/fleets/stores/slice';
import { doCreateFleet, doGetFleets, doRemoveFleets, doUpdateFleet } from '../modules/fleets/stores/thunk';
import { getFleetsByHub } from '../modules/fleets/api';
import FleetForm from '../components/Forms/FleetForm';
import { getStoredPageSize } from '../hooks/useColumnConfig';
import FleetsTable from '../components/Tables/FleetsTable';

const initStateModal = {
  open: false,
  fleet: null,
  action: 'ide',
};

const FleetsPage = () => {
  const { t } = useTranslation('fleet');
  const { t: tc } = useTranslation('common');
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const isFetching = useSelector((state) => state.fleet.isFetching);
  const status = useSelector((state) => state.fleet.status);
  const action = useSelector((state) => state.fleet.action);
  const metadata = useSelector((state) => state.fleet.metadata);
  const fleets = useSelector((state) => selectAllFleets(state));
  const [searchValue, setSearchValue] = useState('');
  const [searchParams] = useSearchParams();

  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [stateModal, setStateModal] = useState(initStateModal);
  // OVO-144 — per-vehicle usage-stats modal (opened from the By-hub view).
  const [statsModal, setStatsModal] = useState({ open: false, vehicle: null });
  const [isActionOpen, setIsActionOpen] = useState(false);
  const tableConfigRef = useRef({});

  // OVO-136 — "List" vs "By hub" (where are my vehicles) view. By hub is the
  // default — operators think in terms of "what's at each velostation".
  const [viewMode, setViewMode] = useState('byHub');
  const [hubGroups, setHubGroups] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(false);
  useEffect(() => {
    if (viewMode !== 'byHub') return;
    let alive = true;
    setLoadingGroups(true);
    getFleetsByHub()
      .then(({ response }) => {
        if (alive && response) setHubGroups(response.groups || []);
      })
      .finally(() => alive && setLoadingGroups(false));
    return () => {
      alive = false;
    };
    // re-fetch when a fleet is created/updated/removed so the grouping stays fresh
  }, [viewMode, action, status]);

  const query = Object.fromEntries(searchParams);
  const currentPage = query.current || 1;
  // Page size lives under the gear (house rule) — the drawer persists it per
  // table; the URL still wins so shared links keep their pagination.
  const itemsPerPage = query.pageSize || getStoredPageSize('fleets-columns', PAGE_SIZE);
  const pagination = {
    current: currentPage || 1,
    pageSize: Number(itemsPerPage),
    total: metadata.totalItems || fleets.length,
    showSizeChanger: false,
    showTotal: (total) => tc('totalItems', { total, entity: t('title') }),
  };

  const onSelectChange = (newSelectedRowKeys) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const onClickAction = () => {
    selectedRowKeys.length > 0 && setIsActionOpen(!isActionOpen);
  };

  const contentPopoverAction = () => {
    return (
      <div className="buttons">
        <Popconfirm
          title={tc('areYouSureRemove')}
          onConfirm={() => handleClickRemoveFleets()}
          okText={tc('yes')}
          cancelText={tc('no')}
        >
          <Button type="link">{tc('remove')}</Button>
        </Popconfirm>
      </div>
    );
  };

  const handleClickRemoveFleets = () => {
    dispatch(doRemoveFleets({ ids: selectedRowKeys }));
  };

  useEffect(() => {
    if (['create', 'update', 'removeMany']?.includes(action) && status === 'succeeded') {
      dispatch(doGetFleets({ page: currentPage, take: itemsPerPage, ...query }));
      if (isActionOpen) setIsActionOpen(false);
      handleCancelModal();
    }
  }, [isFetching]);

  useEffect(() => {
    setSearchValue(query.q || '');
  }, []);

  useEffect(() => {
    dispatch(doGetFleets({ page: currentPage, take: itemsPerPage, ...query }));
  }, []);

  const handleCancelModal = () => {
    setStateModal(initStateModal);
  };

  const pushTo = (pushProps) => {
    navigate({
      pathname: pushProps.path || location.pathname,
      search: queryString.stringify(pushProps.query || {}, {
        skipNull: true,
      }),
      state: pushProps.state,
    });
  };

  const handleSubmitModal = (data) => {
    if (stateModal.action === 'new') {
      dispatch(doCreateFleet(data));
    } else {
      data.id = stateModal.fleet.id;
      dispatch(doUpdateFleet(data));
    }
  };

  const handleFilter = (params, updatePath) => {
    delete params.pageSizeOptions;
    if (updatePath) {
      pushTo({ query: params });
    }
    const newCurrentPage = Number(params?.current || 1);
    const newItemsPerPage = Number(params?.pageSize) || PAGE_SIZE;
    dispatch(
      doGetFleets({
        page: params.current || newCurrentPage,
        take: newItemsPerPage,
        ...params,
      }),
    );
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSearch && handleSearch();
    }
  };

  const handleTableChange = (params) => {
    const newParams = { ...query, ...params };
    handleFilter(newParams, true);
  };

  const handleChangeSearch = (e) => {
    setSearchValue(e.target.value);
  };

  const handleSearch = () => {
    handleFilter({ q: searchValue || '' }, true);
  };

  const onClickNew = () => {
    setStateModal({
      ...stateModal,
      open: true,
      fleet: null,
      action: 'new',
    });
  };

  const onClickEdit = (record) => {
    setStateModal({
      ...stateModal,
      open: true,
      fleet: record,
      action: 'edit',
    });
  };

  const withAction = (record) => {
    return (
      <div className="actions">
        <Button type="link" className="edit" onClick={() => onClickEdit(record)}>
          <ReactSVG className="icon" src="/icons/edit.svg" />
        </Button>
        <Button type="link" className="more">
          <ReactSVG className="icon" src="/icons/more.svg" />
        </Button>
      </div>
    );
  };

  return (
    <Spin spinning={isFetching}>
      <div className="FleetsPage Content">
        <div className="ContentHeader">
          <div className="HeaderTitle">{t('title')}</div>
          <div className="HeaderToolBar">
            <div className="ToolBarButtons" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <Segmented
                value={viewMode}
                onChange={setViewMode}
                options={[
                  { label: t('viewList', 'List'), value: 'list' },
                  { label: t('viewByHub', 'By hub'), value: 'byHub' },
                ]}
              />
              <Button className="CreateButton ButtonPrimary" type="primary" size="small" onClick={onClickNew}>
                {t('createNewFleet')}
                <ReactSVG className="icon" src="/icons/plus.svg" />
              </Button>
            </div>
          </div>
        </div>
        {viewMode === 'list' && (
          <div className="page-header-table">
            <div className="search">
              <Input
                onChange={handleChangeSearch}
                onBlur={handleSearch}
                placeholder={tc('search')}
                onKeyDown={handleKeyDown}
                suffix={<img src="/icons/search.svg" />}
                disabled={isFetching}
                value={searchValue}
              />
            </div>
            <div className="buttons">
              <Popover
                open={selectedRowKeys?.length > 0 && isActionOpen}
                content={contentPopoverAction}
                overlayClassName="DriversPagePopoverAction"
                placement="bottom"
              >
                <Button className="FilterButton ButtonFilter" onClick={onClickAction}>
                  {selectedRowKeys?.length > 0 && <div className="count">{selectedRowKeys.length}</div>}
                  {tc('action')} <ReactSVG className="icon" src="/icons/arrow_drop_down.svg" />
                </Button>
              </Popover>
              <Button
                type="text"
                onClick={() => tableConfigRef.current?.openDrawer?.()}
                style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
              >
                <ReactSVG className="icon" src="/icons/settings.svg" />
              </Button>
            </div>
          </div>
        )}

        <div className="ContentMain">
          {viewMode === 'list' ? (
            <FleetsTable
              selectedRowKeys={selectedRowKeys}
              dataSource={fleets}
              pagination={pagination}
              withAction={withAction}
              rowKey="id"
              isFetching={false}
              onSelectChange={onSelectChange}
              onChange={handleTableChange}
              configRef={tableConfigRef}
              onPageSizeChange={(size) => handleTableChange({ pageSize: size, current: 1 })}
            />
          ) : (
            <FleetsByHubView
              groups={hubGroups}
              loading={loadingGroups}
              t={t}
              tc={tc}
              onVehicleClick={(vehicle) => setStatsModal({ open: true, vehicle })}
            />
          )}
        </div>

        <Modal open={stateModal.open} onCancel={handleCancelModal} footer={[]} width={792} forceRender>
          <FleetForm
            handleSubmit={handleSubmitModal}
            handleCancel={handleCancelModal}
            fleet={stateModal.fleet}
            action={stateModal.action}
            isFetching={isFetching}
          />
        </Modal>

        <VehicleStatsModal
          open={statsModal.open}
          vehicle={statsModal.vehicle}
          t={t}
          tc={tc}
          onClose={() => setStatsModal({ open: false, vehicle: null })}
          onEdit={() => {
            const full = fleets.find((f) => f.id === statsModal.vehicle?.id) || statsModal.vehicle;
            setStatsModal({ open: false, vehicle: null });
            setStateModal({ open: true, fleet: full, action: 'edit' });
          }}
        />
      </div>
    </Spin>
  );
};

// OVO-136 — "where are my vehicles": every hub with the vehicles based there.
const STATUS_COLOR = { FUNCTIONAL: 'green', MAINTENANCE: 'orange', UNFUNCTIONAL: 'red' };

function FleetsByHubView({ groups, loading, t, tc, onVehicleClick }) {
  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 40 }}>
        <Spin />
      </div>
    );
  }
  if (!groups || groups.length === 0) {
    return <Empty description={t('noHubs', 'No hubs')} />;
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {groups.map((g) => (
        <Card
          key={g.hub.id ?? 'unassigned'}
          size="small"
          title={
            <span>
              {g.hub.name}
              {g.hub.tag && (
                <Tag style={{ marginLeft: 8 }}>{tc(`hubTags.${g.hub.tag}`, { defaultValue: g.hub.tag })}</Tag>
              )}
              <span style={{ color: 'var(--text-muted)', fontWeight: 400, marginLeft: 6 }}>
                · {g.vehicleCount} {t('vehicles', 'vehicles')}
              </span>
            </span>
          }
          extra={
            g.hub.startAddress ? (
              <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>{g.hub.startAddress}</span>
            ) : null
          }
        >
          {g.vehicles.length === 0 ? (
            <span style={{ color: '#bbb', fontStyle: 'italic' }}>{t('noVehiclesHere', 'No vehicles here')}</span>
          ) : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {g.vehicles.map((v) => (
                <div
                  key={v.id}
                  onClick={() => onVehicleClick(v)}
                  title={t('viewStats', 'View usage stats')}
                  style={{
                    cursor: 'pointer',
                    border: '1px solid #e8e8e8',
                    borderRadius: 8,
                    padding: '8px 12px',
                    minWidth: 190,
                  }}
                >
                  <div style={{ fontWeight: 600 }}>{v.name}</div>
                  <div style={{ marginTop: 6, display: 'flex', gap: 6, alignItems: 'center' }}>
                    <Tag>{tc(`fleetTags.${v.tag}`, { defaultValue: v.tag })}</Tag>
                    <Tag color={STATUS_COLOR[v.status] || 'default'}>
                      {tc(`fleetStatuses.${v.status}`, { defaultValue: v.status })}
                    </Tag>
                  </div>
                  {/* OVO-144 — inline all-time usage summary. */}
                  {v.stats && (
                    <div style={{ marginTop: 6, fontSize: 12, color: 'var(--text-muted)' }}>
                      {v.stats.trips > 0 ? (
                        <>
                          {v.stats.totalKm} km · {v.stats.trips} {t('tripsShort', 'trips')}
                          {v.stats.lastUsed ? ` · ${v.stats.lastUsed}` : ''}
                        </>
                      ) : (
                        <span style={{ fontStyle: 'italic' }}>{t('neverUsed', 'never used')}</span>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </Card>
      ))}
    </div>
  );
}

// OVO-144 — per-vehicle all-time usage stats (computed server-side from the
// routes the vehicle ran). Read-only; an Edit button hops to the FleetForm.
function StatBox({ label, value }) {
  return (
    <div style={{ flex: '1 1 33%', minWidth: 130, padding: '8px 4px' }}>
      <div style={{ color: 'var(--text-muted)', fontSize: 12 }}>{label}</div>
      <div style={{ fontWeight: 600, fontSize: 18 }}>{value}</div>
    </div>
  );
}

function VehicleStatsModal({ open, vehicle, t, tc, onClose, onEdit }) {
  const s = vehicle?.stats;
  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={
        vehicle ? (
          <span>
            {vehicle.name}{' '}
            <Tag style={{ marginLeft: 6 }}>{tc(`fleetTags.${vehicle.tag}`, { defaultValue: vehicle.tag })}</Tag>
          </span>
        ) : (
          ''
        )
      }
      width={560}
      footer={[
        <Button key="edit" onClick={onEdit}>
          {tc('edit', { defaultValue: 'Edit' })}
        </Button>,
        <Button key="close" type="primary" onClick={onClose}>
          {tc('close', { defaultValue: 'Close' })}
        </Button>,
      ]}
    >
      {!s || s.trips === 0 ? (
        <Empty description={t('neverUsedLong', 'This vehicle has not run any routes yet.')} />
      ) : (
        <>
          <div style={{ display: 'flex', flexWrap: 'wrap' }}>
            <StatBox label={t('totalKm', 'Total km')} value={`${s.totalKm} km`} />
            <StatBox label={t('trips', 'Trips')} value={s.trips} />
            <StatBox label={t('drivenHours', 'Driven hours')} value={`${s.drivenHours} h`} />
            <StatBox label={t('co2', 'CO₂')} value={`${s.co2Kg} kg`} />
            <StatBox label={t('distinctDrivers', 'Drivers')} value={s.distinctDrivers} />
            <StatBox
              label={t('mostDrivenBy', 'Most driven by')}
              value={s.topDriver ? `${s.topDriver.name} (${s.topDriver.trips})` : '—'}
            />
          </div>
          <div style={{ marginTop: 12, color: 'var(--text-muted)', fontSize: 13 }}>
            {t('usedFromTo', 'Used')} {s.firstUsed} → {s.lastUsed}
          </div>
          <div style={{ marginTop: 6, color: '#bbb', fontSize: 11 }}>
            {t('statsAllTimeNote', 'All-time, across dispatched/completed routes.')}
          </div>
        </>
      )}
    </Modal>
  );
}

export default FleetsPage;
